import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    project: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    api.getSiteData().then(response => {
      setContent(response.data[language].contact);
    }).catch(err => console.error('Error loading contact data:', err));
  }, [language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await api.submitContact(formData);
      toast({
        title: language === 'fr' ? 'Message envoyé !' : 'Message sent!',
        description: language === 'fr' 
          ? 'Je vous répondrai dans les plus brefs délais.'
          : "I'll get back to you as soon as possible."
      });
      setFormData({ name: '', email: '', project: '' });
    } catch (error) {
      const raw = error?.response?.data?.detail;
      const detail =
        typeof raw === 'string'
          ? raw
          : Array.isArray(raw)
            ? raw.map((x) => x?.msg || x).filter(Boolean).join(' ')
            : error?.message;
      toast({
        title: language === 'fr' ? 'Envoi impossible' : 'Could not send',
        description:
          detail ||
          (language === 'fr'
            ? 'Réessayez ou écrivez à contact@loranrix.com.'
            : 'Try again or email contact@loranrix.com.'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!content) return <div className="py-32 bg-[#0a0a0b]" />;

  return (
    <section id="contact" className="relative py-32 bg-gradient-to-b from-[#0f0f10] to-[#0a0a0b] overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6"
          >
            <span className="text-sm text-cyan-400 font-medium font-mono">
              {language === 'fr' ? 'CONTACT' : 'GET IN TOUCH'}
            </span>
          </motion.div>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 text-balance hyphens-none px-1">
            {content.title}
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8 text-pretty px-1">
            {content.subtitle}
          </p>

          {/* Email button */}
          <motion.a
            href={`mailto:${content.email}`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex flex-wrap items-center justify-center gap-2 px-6 py-3 bg-cyan-500/10 text-cyan-400 rounded-lg font-medium hover:bg-cyan-500/20 transition-all duration-300 border border-cyan-500/20 max-w-full min-w-0 break-words text-center"
          >
            <Mail className="w-5 h-5" />
            {content.email}
          </motion.a>
        </motion.div>

        {/* Contact form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative"
        >
          <div className="relative p-8 md:p-12 bg-gradient-to-br from-[#1a1a1b] to-[#0f0f10] rounded-2xl border border-cyan-500/10 overflow-hidden">
            {/* Glow effect */}
            <motion.div
              animate={{
                opacity: [0.05, 0.1, 0.05]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
              className="absolute inset-0"
              style={{
                background: 'radial-gradient(circle at top, rgba(0, 217, 255, 0.1), transparent 60%)'
              }}
            />

            <form onSubmit={handleSubmit} className="relative z-10 space-y-6">
              <div>
                <label className="block text-white font-medium mb-2">
                  {content.formLabels.name}
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 transition-colors"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2">
                  {content.formLabels.email}
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 transition-colors"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2">
                  {content.formLabels.project}
                </label>
                <textarea
                  required
                  value={formData.project}
                  onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                  rows={5}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 transition-colors resize-none"
                  placeholder={language === 'fr' ? 'Décrivez votre projet...' : 'Describe your project...'}
                />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-cyan-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                    />
                    {language === 'fr' ? 'Envoi...' : 'Sending...'}
                  </>
                ) : (
                  <>
                    {content.formLabels.submit}
                    <Send className="w-5 h-5" />
                  </>
                )}
              </motion.button>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
